import { useMemo, useState } from 'react'
import type { RowType } from '../../typings'
import { useGetOrderUrl } from '@user/components/Scheduling'
import { useGetLocationUrl } from '@user/store/authorization'

type MenuOptions = {
  canCancel: boolean
  canComplete: boolean
  canReschedule: boolean
}

type Props = {
  row?: RowType
  entityClosed: boolean
  anchorEl: HTMLElement | null
}

export function useData(props: Props) {
  const { row, anchorEl, entityClosed } = props

  const [activeDialog, setActiveDialog] = useState<'complete' | 'cancel'>()

  const open = Boolean(anchorEl)
  const dialogOpen = !!activeDialog

  const status = row?.status
  const hasLocation = Boolean(row?.locationId)
  const allowActions = row?.appointmentType === 'Estimate'

  const buttonState = useMemo<MenuOptions>(() => {
    if (entityClosed || !allowActions) {
      return {
        canCancel: false,
        canComplete: false,
        canReschedule: false,
      }
    }

    switch (status) {
      case 'Not Scheduled':
        return {
          canCancel: true,
          canComplete: false,
          canReschedule: hasLocation,
        }
      case 'Scheduled':
        return {
          canCancel: true,
          canComplete: true,
          canReschedule: hasLocation,
        }
      case 'Completed':
      case 'Cancelled':
      case 'Invoiced':
      default:
        return {
          canCancel: false,
          canComplete: false,
          canReschedule: false,
        }
    }
  }, [allowActions, entityClosed, hasLocation, status])

  const linkToOrder = row?.status !== 'Invoiced'
  const orderUrl = useGetOrderUrl(row?.appointmentId ?? '')
  const locationUrl = useGetLocationUrl(row?.locationId ?? '', false)

  return {
    ...buttonState,
    open,
    orderUrl,
    dialogOpen,
    locationUrl,
    linkToOrder,
    activeDialog,
    setActiveDialog,
  }
}

export type UseData = ReturnType<typeof useData>
